import { useId, useMemo, useState } from 'react';

import { ColumnDef, PaginationState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { addDays, format, startOfDay, sub } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ActivityMatter } from '@/../../backend/src/usage/dto/activity-overview.response.dto';
import { useDownloadActivityData } from '@/api/mutations/useDownloadActivityData';
import { useGetActivityMatters, useGetActivityOverview } from '@/api/queries/useGetActivityOverview';
import { Icon } from '@/new/components/icon';
import { SettingsSectionHeading } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Calendar } from '@/new/components/shadcn/calendar';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Switch } from '@/new/components/shadcn/switch';
import { ServerSideScrollableTable } from '@/new/components/table/server-side-scrollable-table';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { cn } from '@/new/lib/utils';

const DATE_PRESETS = [
  { label: 'Last 24 hours', duration: { days: 1 } },
  { label: 'Last 7 days', duration: { weeks: 1 } },
  { label: '2W', duration: { weeks: 2 } },
  { label: '1M', duration: { months: 1 } },
  { label: '3M', duration: { months: 3 } },
  { label: '1Y', duration: { years: 1 } },
  { label: 'All time', duration: undefined },
] as const;
const PAGE_SIZE = 15;
const number = new Intl.NumberFormat('en-GB');
const compact = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 });
const month = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

type ChartDatum = { month: string; label: string; value: number };
type TooltipRow = { label: string; value: number };

function BreakdownTooltip({ rows }: { rows: TooltipRow[] }) {
  return (
    <div className="w-44 space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex justify-between gap-6">
          <span>{row.label}</span>
          <span className="font-medium tabular-nums">{number.format(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryMetricCard({
  title,
  value,
  data,
  tooltip,
}: {
  title: React.ReactNode;
  value: number;
  data: ChartDatum[];
  tooltip: React.ReactNode;
}) {
  const gradientId = `summary-metric-${useId().replaceAll(':', '')}`;
  const chartData = [
    { month: 'start-2', label: '', value: 0 },
    { month: 'start-1', label: '', value: 0 },
    ...data,
    { month: 'end-1', label: '', value: 0 },
    { month: 'end-2', label: '', value: 0 },
  ];

  return (
    <div className="relative flex min-w-0 items-end justify-between gap-4 overflow-hidden rounded-xl border border-gray-200/70 bg-white px-5 py-4">
      <div className="absolute inset-x-0 bottom-[12px] h-0.5 bg-teal-400" aria-hidden="true" />
      <div className="relative z-10 min-w-0">
        <div className="flex items-center gap-1.5 text-sm whitespace-nowrap text-gray-600">
          {title}
          <TooltipIcon side="right" tooltipContent={tooltip} contentClassName="max-w-72" />
        </div>
        <p className="mt-0.5 pb-2 text-2xl font-semibold tabular-nums">{number.format(value)}</p>
      </div>
      <div className="relative h-13 w-38 shrink-0 translate-y-[7px] bg-white" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-teal-400)" stopOpacity={0.25} />
                <stop offset="100%" stopColor="var(--color-teal-400)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-teal-400)"
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MetricChartCard<T extends ChartDatum>({
  title,
  titleTooltip,
  value,
  data,
  className,
  getTooltipRows = (item) => [{ label: title, value: item.value }],
}: {
  title: string;
  titleTooltip?: string;
  value: number;
  data: T[];
  className?: string;
  getTooltipRows?: (item: T) => TooltipRow[];
}) {
  const [isCumulative, setIsCumulative] = useState(true);
  const chartData = useMemo(() => {
    if (!isCumulative) return data;

    const totals: Record<string, number> = {};
    return data.map(
      (item) =>
        Object.fromEntries(
          Object.entries(item).map(([key, itemValue]) => {
            if (typeof itemValue !== 'number') return [key, itemValue];

            totals[key] = (totals[key] ?? 0) + itemValue;
            return [key, totals[key]];
          }),
        ) as T,
    );
  }, [data, isCumulative]);
  const gradientId = `activity-${title.toLowerCase().replaceAll(' ', '-')}`;
  const switchId = `${gradientId}-cumulative`;
  return (
    <section className={cn('rounded-xl border border-gray-200/70 bg-white', className)}>
      <div className="px-5.5 pt-4.5 pr-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
            <p>{title}</p>
            {titleTooltip && <TooltipIcon side="right" tooltipContent={titleTooltip} />}
          </div>
          <div className="flex items-center gap-0.75">
            <label htmlFor={switchId} className="cursor-pointer text-xs text-gray-600">
              Cumulative
            </label>
            <Switch
              className="scale-70 data-[state=checked]:bg-gray-700"
              id={switchId}
              checked={isCumulative}
              onCheckedChange={setIsCumulative}
            />
          </div>
        </div>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">{number.format(value)}</p>
      </div>

      <div className="px-2 pt-4.5 pr-5 pb-1">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-teal-300)" stopOpacity={0.2} />
                <stop offset="80%" stopColor="var(--color-teal-300)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={32}
              tickMargin={12}
              tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
              height={48}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={48}
              allowDecimals={false}
              tickFormatter={(value: number) => compact.format(value)}
              tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
            />
            <Tooltip
              cursor={{ stroke: 'var(--color-gray-200)' }}
              wrapperStyle={{ zIndex: 20 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0].payload as T;
                return (
                  <div className="min-w-44 rounded-lg border bg-white p-3 text-sm shadow-lg/4">
                    <p className="mb-2 border-b pb-2 font-medium text-gray-800">{item.label}</p>
                    <div className="space-y-1.5">
                      {getTooltipRows(item).map((row) => (
                        <div key={row.label} className="flex justify-between gap-6 text-gray-600">
                          <span>{row.label}</span>
                          <span className="font-medium text-gray-900">{number.format(row.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-teal-400)"
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ChronologiesChartCard({
  factTotal,
  documentTotal,
  data,
}: {
  factTotal: number;
  documentTotal: number;
  data: Array<ChartDatum & { factChronologies: number; documentChronologies: number }>;
}) {
  return (
    <MetricChartCard
      title="Chronologies"
      value={factTotal + documentTotal}
      data={data}
      getTooltipRows={(item) => [
        { label: 'Fact chronologies', value: item.factChronologies },
        { label: 'Document chronologies', value: item.documentChronologies },
      ]}
    />
  );
}

const matterColumns: ColumnDef<ActivityMatter>[] = [
  {
    accessorKey: 'matterName',
    header: 'Matter',
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="block max-w-72 truncate font-medium text-gray-800">{getValue<string>()}</span>
    ),
  },
  ...(
    [
      ['kimMessages', 'Kim queries'],
      ['documents', 'Documents'],
      ['chronologies', 'Chronologies'],
      ['inconsistencies', 'Inconsistencies'],
      ['entities', 'Entities'],
    ] as const
  ).map<ColumnDef<ActivityMatter>>(([id, label]) => ({
    id,
    header: () => <span className="ml-auto">{label}</span>,
    accessorFn: (row) => (id === 'chronologies' ? row.chronologies + row.docChronologies : row[id]),
    enableSorting: false,
    cell: ({ getValue }) => <span className="block text-right tabular-nums">{number.format(getValue<number>())}</span>,
  })),
];

export function ActivityDashboard({
  organisationId,
  isViewingOtherOrg = false,
}: {
  organisationId: string;
  isViewingOtherOrg?: boolean;
}) {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [selectedPreset, setSelectedPreset] = useState<string>('1M');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const to = new Date();
    return { from: sub(to, { months: 1 }), to };
  });
  const [filters, setFilters] = useState<{ startDate?: string; endDate?: string }>(() => ({
    startDate: dateRange.from?.toISOString(),
    endDate: dateRange.to?.toISOString(),
  }));
  const { data, isLoading, isFetching, isError } = useGetActivityOverview(organisationId, filters);
  const { data: matterData, isLoading: areMattersLoading } = useGetActivityMatters(
    organisationId,
    pagination.pageIndex + 1,
    pagination.pageSize,
    filters,
  );
  const { fetchDownload, isLoading: isDownloading } = useDownloadActivityData();
  const matterTable = useReactTable({
    data: matterData?.rows ?? [],
    columns: matterColumns,
    state: { pagination },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: matterData?.pageCount ?? 0,
    onPaginationChange: setPagination,
    getRowId: (row) => row.matterId,
  });

  if (isLoading) {
    return (
      <div className="flex h-full animate-pulse items-center justify-center gap-2 text-sm text-gray-500">
        <Icon className="lucide--loader animate-spin ease-in-out" />
        <span>Loading activity data...</span>
      </div>
    );
  }
  if (!data) {
    return (
      <Empty className="h-full">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--triangle-alert" />
          </EmptyMedia>
          <EmptyTitle>Activity unavailable</EmptyTitle>
          <EmptyDescription>Organisation activity could not be loaded.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const timeSeries = data.timeSeries;
  const isPersonal = data.scope === 'personal';
  const series = (value: (point: (typeof timeSeries)[number]) => number): ChartDatum[] =>
    timeSeries.map((point) => ({
      month: point.month,
      label: month.format(new Date(`${point.month.slice(0, 10)}T00:00:00Z`)),
      value: value(point),
    }));

  const chartLabel = (date: string) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      ...(data.granularity === 'hour'
        ? ({ day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' } as const)
        : data.granularity === 'day'
          ? ({ day: 'numeric', month: 'short' } as const)
          : ({ month: 'short', year: '2-digit' } as const)),
    }).format(new Date(date));
  const chartTimeSeries = data.chartTimeSeries;
  const chartSeries = (value: (point: (typeof chartTimeSeries)[number]) => number): ChartDatum[] =>
    chartTimeSeries.map((point) => ({ month: point.date, label: chartLabel(point.date), value: value(point) }));

  return (
    <div className="space-y-4">
      <SettingsSectionHeading
        title="Activity"
        description={
          isPersonal
            ? 'See activity across your matters.'
            : isViewingOtherOrg
              ? 'See how this organisation is using Wexler.'
              : 'See how your organisation is using Wexler.'
        }
        inset={false}
        action={
          <Button
            disabled={isDownloading}
            onClick={() => fetchDownload({ organisationId, ...filters })}
            className="mt-2"
          >
            <Icon className={isDownloading ? 'lucide--loader animate-spin ease-in-out' : 'lucide--download'} />
            Download activity
          </Button>
        }
      />

      {!isPersonal && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <SummaryMetricCard
            title={<span>Users</span>}
            value={data.totals.users}
            data={series((point) => point.users)}
            tooltip={
              <BreakdownTooltip
                rows={[
                  { label: 'Current users', value: data.totals.users },
                  { label: 'Blocked users', value: data.totals.blockedUsers },
                ]}
              />
            }
          />
          <SummaryMetricCard
            title="Active users last 30 days"
            value={data.totals.activeUsers}
            data={series((point) => point.activeUsers)}
            tooltip="Current users who created a matter, uploaded documents, created a chronology, or asked Kim in the last 30 days. The preview shows unique active users by month."
          />
          <SummaryMetricCard
            title={<span>Matters</span>}
            value={data.totals.matters}
            data={series((point) => point.matters)}
            tooltip={
              <BreakdownTooltip
                rows={[
                  { label: 'Active', value: data.totals.matters },
                  { label: 'Drafts', value: data.totals.draftMatters },
                  { label: 'Deleted', value: data.totals.deletedMatters },
                ]}
              />
            }
          />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 px-4 pr-3">
        <h2 className="font-medium text-gray-900">Activity over time period</h2>
        <div className="flex flex-wrap items-center gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button className="min-w-0 justify-end overflow-hidden border-0 bg-transparent px-1.5 py-1 text-[13px] font-normal">
                <Icon className="lucide--calendar-range mb-px" />
                <span className="truncate">
                  {dateRange.from && dateRange.to
                    ? `${format(dateRange.from, 'd MMM yyyy')} – ${format(dateRange.to, 'd MMM yyyy')}`
                    : 'Select dates'}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="max-w-none bg-white p-1">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  setDateRange(range);
                  if (range.from && range.to) {
                    setSelectedPreset('Custom');
                    setPagination((current) => ({ ...current, pageIndex: 0 }));
                    setFilters({
                      startDate: startOfDay(range.from).toISOString(),
                      endDate: addDays(startOfDay(range.to), 1).toISOString(),
                    });
                  }
                }}
                disabled={{ after: new Date() }}
                buttonVariant="default"
                numberOfMonths={2}
                required
              />
            </PopoverContent>
          </Popover>
          <div
            role="group"
            aria-label="Activity date range"
            className="inline-flex flex-wrap items-center rounded-[10px] bg-gray-100 p-0.5"
          >
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                aria-pressed={selectedPreset === preset.label}
                className={cn(
                  'inline-flex items-center justify-center rounded-[8px] px-2.75 pt-1.25 pb-1 text-xs whitespace-nowrap text-gray-500 transition-colors hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-teal-500',
                  selectedPreset === preset.label && 'bg-white text-gray-900 shadow-lg/3',
                )}
                onClick={() => {
                  const to = new Date();
                  const from = preset.duration ? sub(to, preset.duration) : undefined;
                  setSelectedPreset(preset.label);
                  setPagination((current) => ({ ...current, pageIndex: 0 }));
                  setDateRange({ from, to: from ? to : undefined });
                  setFilters(from ? { startDate: from.toISOString(), endDate: to.toISOString() } : {});
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {isError ? (
        <p role="alert" className="px-4 text-sm text-red-600">
          Activity could not be loaded for this date range. Please try again.
        </p>
      ) : (
        <div aria-busy={isFetching} className={cn('grid gap-3 lg:grid-cols-2', isFetching && 'animate-pulse')}>
          <MetricChartCard
            title="Kim messages"
            titleTooltip="Includes messages in sample matters and errored threads. Does not include deleted threads."
            value={data.totals.kimMessages}
            data={chartSeries((point) => point.kimMessages)}
            className="lg:col-span-2"
          />
          <MetricChartCard
            title="Documents"
            value={data.totals.documents}
            data={chartSeries((point) => point.documents)}
          />
          <ChronologiesChartCard
            factTotal={data.totals.chronologies}
            documentTotal={data.totals.docChronologies}
            data={data.chartTimeSeries.map((point) => ({
              month: point.date,
              label: chartLabel(point.date),
              value: point.chronologies + point.docChronologies,
              factChronologies: point.chronologies,
              documentChronologies: point.docChronologies,
            }))}
          />
          <MetricChartCard
            title="Inconsistencies"
            value={data.totals.inconsistencies}
            data={chartSeries((point) => point.inconsistencies)}
          />
          <MetricChartCard
            title="Entities"
            value={data.totals.entities}
            data={chartSeries((point) => point.entities)}
          />
        </div>
      )}

      <section className="mt-7">
        <div className="mb-4 px-4">
          <h2 className="font-medium text-gray-900">Activity by matter</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Activity for the selected time period.{!isPersonal && ' Restricted matters are shown by ID.'}
          </p>
        </div>

        <ServerSideScrollableTable
          table={matterTable}
          searchQuery=""
          onSearchQueryChange={() => undefined}
          plural="matters"
          loading={areMattersLoading}
          totalCount={matterData?.totalCount ?? 0}
          showSearch={false}
          className="h-[44rem] px-2"
        />
      </section>
    </div>
  );
}
